/**
 * IndexedDB-backed private key storage for browser environments.
 *
 * Stores CryptoKey objects directly via the structured clone algorithm —
 * the key remains non-extractable in the JS runtime yet is durable across
 * page reloads without ever leaving the device as raw bytes.
 */

const DB_NAME = "ccs-crypto-v1";
const STORE_NAME = "keys";
const PRIVATE_KEY_PREFIX = "pk:";
const PUBLIC_KEY_PREFIX = "pub:";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IDB open blocked"));
  });
}

/** Persist a private CryptoKey for the given uid into IndexedDB. */
export async function storePrivateKey(uid: string, key: CryptoKey): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(key, PRIVATE_KEY_PREFIX + uid);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Load the private CryptoKey for the given uid from IndexedDB. Returns null if absent. */
export async function loadPrivateKey(uid: string): Promise<CryptoKey | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(PRIVATE_KEY_PREFIX + uid);
    req.onsuccess = () => resolve((req.result as CryptoKey | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** Remove the private key for the given uid (e.g. sign-out). */
export async function clearPrivateKey(uid: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(PRIVATE_KEY_PREFIX + uid);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Persist the SPKI-base64 public key alongside the private key for the given
 * uid. The private key stored via {@link storePrivateKey} is non-extractable
 * (web generates keypairs with extractable=false), so unlike mobile there is
 * no way to re-derive the public key from the private CryptoKey later. This
 * companion value lets the web CryptoContext self-heal (re-publish to
 * Firestore) if the public key never made it to the server after the
 * private key was already written locally.
 */
export async function storePublicKey(uid: string, publicKeyBase64: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(publicKeyBase64, PUBLIC_KEY_PREFIX + uid);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Load the locally-held SPKI-base64 public key for the given uid. Returns null if absent. */
export async function loadPublicKey(uid: string): Promise<string | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(PUBLIC_KEY_PREFIX + uid);
    req.onsuccess = () => resolve((req.result as string | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** Remove the locally-held public key for the given uid (e.g. key rotation, sign-out). */
export async function clearPublicKey(uid: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(PUBLIC_KEY_PREFIX + uid);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
